import { Promise, strings } from "@securionpay-internal/commons-js";
import ControllerFrame from "./ControllerFrame";
import json from "./../json";
import ComponentFrame from "../component/ComponentFrame";
import { isOriginAllowed } from "../../securionpayOrigins";
import awaitDocumentBody from "../dom/awaitDocumentBody";
import { actionMatches } from "../../utils/actionMatches";
import { COMPANY_NAME } from "../../env";
import { Charge } from "../types/response/Charge";
import { ErrorResponse } from "../types/response/ErrorResponse";
import { Token } from "../types/response/Token";

import { MerchantConfig } from "../types/response/MerchantConfig";
import { PayPalInitiate } from "../types/response/PayPalInitiate";
import {
    UserActionChargeRetrieveOptions,
    UserActionCreateApplePaySessionOptions, UserActionInitiatePayPalOptions, UserActionPaymentMethodRetrieveOptions, UserActionPaymentMethodUpdateOptions,
    UserActionTokenizeOptions,
    UserActionTokenizeV1Options,
    UserActionType,
    UserActionUpdateTokenOptions
} from "../types/UserAction";
import { ControllerOptions, TrackingOptions } from "../types/SecurionPayOptions";
import Frame from "../Frame";
import { PaymentMethod } from "../types/response/PaymentMethod";
import { ComponentType } from "../types";

export default class Controller {
    _id: string;
    _frames: { [frameId: string]: any };
    _requests: { [requestId: string]: any };
    _controllerFrame: any;

    constructor(publicKey: string, options: ControllerOptions = {}, tracking?: TrackingOptions) {
        this._id = strings.unique(`__private${COMPANY_NAME}Controller`);
        this._controllerFrame = new ControllerFrame(this._id, publicKey, options, tracking);
        this._frames = {};
        this._requests = {};

        awaitDocumentBody().then(body => this._controllerFrame.appendTo(body));
        this._setupMessageListener();
    }

    private _setupMessageListener() {
        window.addEventListener("message", (messageEvent) => {
            if (!isOriginAllowed(messageEvent.origin)) {
                if (this._shouldLogDisallowedOriginAsUnknown(messageEvent.origin)) {
                    console.warn("Message from unknown origin, skipping: ", messageEvent.origin)
                }
                return;
            }

            let messageEventData = json.tryParse(messageEvent.data);
            if (messageEventData) {
                this._handleMessage(messageEventData);
            }
        });
    }

    private _shouldLogDisallowedOriginAsUnknown(origin: string) {
        return !origin.endsWith("kaptcha.com");
    }

    private _handleMessage(messageEventData: { controllerId: string, frameId: string, message: { action: string, payload: any } }) {
        if (this._id !== messageEventData.controllerId || !messageEventData.message.action) {
            return;
        }


        const message = messageEventData.message;
        const payload = message.payload || {};

        if (actionMatches(message, "frame-event")) {
            let frame = this._frames[messageEventData.frameId];
            if (frame) {
                frame.emit(payload.event, payload.data);
            }
        } else if (actionMatches(message, "controller-load")) {
            this._controllerFrame.emit("load");
            Object.keys(this._frames).forEach((frameId) => {
                this._frames[frameId].send({action: "securionpay-controller-load"})
            });
        } else if (actionMatches(message, "user-action-complete")) {
            let request = this._requests[payload.requestId];
            if (request) {
                request.resolve(payload.result);
            }
        } else if (actionMatches(message, "user-action-error")) {
            let request = this._requests[payload.requestId];
            if (request) {
                request.reject(payload.error);
            }
        } else if (actionMatches(message, "frame-error")) {
            throw new Error(message.payload.message);
        } else if (actionMatches(message, "frame-action-complete")) {
            let frame = this._frames[messageEventData.frameId];
            if (frame) {
                frame.resolve(payload.requestId, payload.result);
            }
        }
    }

    action(type: UserActionType.CHARGE_RETRIEVE, options: UserActionChargeRetrieveOptions): Promise<Charge | ErrorResponse>
    action(type: UserActionType.PAYMENT_METHOD_RETRIEVE, options: UserActionPaymentMethodRetrieveOptions): Promise<PaymentMethod | ErrorResponse>
    action(type: UserActionType.PAYMENT_METHOD_UPDATE, options: UserActionPaymentMethodUpdateOptions): Promise<PaymentMethod | ErrorResponse>
    action(type: UserActionType.TOKENIZE, options: UserActionTokenizeOptions): Promise<Token | ErrorResponse>
    action(type: UserActionType.UPDATE_TOKEN, options: UserActionUpdateTokenOptions): Promise<Token | ErrorResponse>
    action(type: UserActionType.TOKENIZE_V1, options: UserActionTokenizeV1Options): Promise<Token | ErrorResponse>
    action(type: UserActionType.CREATE_APPLE_PAY_SESSION, options: UserActionCreateApplePaySessionOptions): Promise<any | ErrorResponse>
    action(type: UserActionType.INITIATE_PAYPAL, options: UserActionInitiatePayPalOptions): Promise<PayPalInitiate | ErrorResponse>
    action(type: UserActionType.GET_MERCHANT_CONFIG, options: {}): Promise<MerchantConfig | ErrorResponse>

    action(type: UserActionType, options = {}): Promise<any | ErrorResponse> {
        let requestId = strings.unique(type);

        let promise = new Promise((resolve, reject) => {
            this._requests[requestId] = {
                resolve,
                reject
            }
        });

        this._controllerFrame.send({
            action: "securionpay-user-action",
            payload: {requestId, type, options}
        });

        return promise;
    }

    createComponentFrame(componentName: ComponentType, options = {}): ComponentFrame {
        let frame = new ComponentFrame(this._id, componentName, options);
        this._setupFrame(frame);
        this._frames[frame.id] = frame;
        return frame;
    }

    addFrame(frame: Frame) {
        this._setupFrame(frame);
        this._frames[frame.id] = frame;
    }

    private _setupFrame(frame: Frame) {
        this._controllerFrame.send({
            action: "securionpay-user-createframe",
            payload: {
                newFrameId: frame.id
            }
        });

        frame.on("load", () => {
            this._controllerFrame.send({
                action: "securionpay-frame-load",
                payload: {
                    loadedFrameId: frame.id
                }
            });

            if (this._controllerFrame.loaded) {
                frame.send({
                    action: "securionpay-controller-load"
                });
            }
        });
    }
}